import { Button } from '@/app/common/components/button/Button'
import { inputVariants } from '@/app/common/components/input/inputVariants'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import { RequestError } from '@/app/common/types_and_interfaces.ts'
import { currencyFormatter } from '@/lib/utils'
import type { FamilyApplication } from '@/queries/family_applications/requests.ts'
import { useUpdateStudentScholarshipAward } from '@/queries/program_purses/useUpdateStudentScholarshipAward.ts'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shad/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shad/ui/form'
import { useToast } from '@/shad/ui/toast/use-toast'
import { zodResolver } from '@hookform/resolvers/zod'
import CurrencyInput from 'react-currency-input-field'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shad/ui/select'
import Label from '@/app/common/components/Label'

type FormValues = {
  amount: string
  programPurseId: string
}

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  student: FamilyApplication | null
  programId: number
}

const parseCurrencyValue = (value?: string) =>
  Number(value?.replace(/[^\d.-]+/g, '') || 0)

export function UpdateScholarshipAwardDialog({
  open,
  onOpenChange,
  student,
  programId,
}: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && student && (
        <UpdateScholarshipAwardDialogContent
          onOpenChange={onOpenChange}
          student={student}
          programId={programId}
        />
      )}
    </Dialog>
  )
}

type ContentProps = Omit<Props, 'open'> & {
  student: FamilyApplication
}

function UpdateScholarshipAwardDialogContent({
  onOpenChange,
  student,
  programId,
}: ContentProps) {
  const { toast } = useToast()

  const studentName = [student.student_first_name, student.student_last_name]
    .filter(Boolean)
    .join(' ')

  const awardablePurses =
    student.program_purses?.filter((purse) => purse.current_award > 0) ?? []

  const defaultProgramPurseId = awardablePurses[0]?.id.toString() ?? ''

  const formSchema = z
    .object({
      amount: z.string().min(1, 'Amount is required'),
      programPurseId: z.string().min(1, 'Purse is required'),
    })
    .superRefine((values, ctx) => {
      const amount = parseCurrencyValue(values.amount)

      if (amount <= 0) {
        ctx.addIssue({
          path: ['amount'],
          code: z.ZodIssueCode.custom,
          message: 'Amount must be greater than 0',
        })
        return
      }

      const purse = awardablePurses.find(
        (p) => p.id.toString() === values.programPurseId
      )

      if (purse && amount === purse.current_award) {
        ctx.addIssue({
          path: ['amount'],
          code: z.ZodIssueCode.custom,
          message: `Amount must be different from ${currencyFormatter.format(
            purse.current_award
          )}`,
        })
      }
    })

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      amount: '',
      programPurseId: defaultProgramPurseId,
    },
  })

  const selectedProgramPurse = awardablePurses.find(
    (purse) => purse.id.toString() === form.watch('programPurseId')
  )

  const currentAward = selectedProgramPurse?.current_award ?? 0

  const mutation = useUpdateStudentScholarshipAward({
    onSuccess: (data) => {
      toast({
        variant: 'default',
        title: 'Award Updated',
        description: `Scholarship award updated to ${currencyFormatter.format(data.new_amount)}.`,
      })

      form.reset()
      onOpenChange(false)
    },
    onError: (error: RequestError) => {
      toast({
        variant: 'destructive',
        title: 'Update Failed',
        description: error.message,
      })
    },
  })

  const handleClose = () => {
    form.reset()
    onOpenChange(false)
  }

  const onSubmit = (values: FormValues) => {
    if (!student.student_id || !selectedProgramPurse) return

    mutation.mutate({
      programId,
      studentId: student.student_id,
      programPurseId: selectedProgramPurse.id,
      amount: parseCurrencyValue(values.amount),
    })
  }

  const newAward = parseCurrencyValue(form.watch('amount'))

  return (
    <DialogContent>
      <DialogHeader className="mb-4">
        <DialogTitle>Update Scholarship Award</DialogTitle>
      </DialogHeader>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="min-w-0 space-y-4"
        >
          {studentName && (
            <div className="space-y-2">
              <Label>Student</Label>
              <p className="text-sm font-medium text-gray-900">{studentName}</p>
            </div>
          )}
          <FormField
            control={form.control}
            name="programPurseId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Purse</FormLabel>
                <FormControl>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select purse" />
                    </SelectTrigger>
                    <SelectContent>
                      {awardablePurses.map((purse) => (
                        <SelectItem key={purse.id} value={purse.id.toString()}>
                          {purse.name} ({purse.id})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="amount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>New award amount</FormLabel>
                <FormControl>
                  <CurrencyInput
                    id="scholarship-award-amount"
                    name={field.name}
                    decimalsLimit={2}
                    min={1}
                    prefix="$"
                    value={field.value}
                    onBlur={field.onBlur}
                    onValueChange={(value) => field.onChange(value || '')}
                    placeholder="Enter amount"
                    required
                    className={inputVariants({ variant: 'text' })}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="bg-gray-50 p-4 rounded-lg space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Current Term Funded Amount:</span>
              <span className="font-medium">
                {currencyFormatter.format(currentAward)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">New Award:</span>
              <span className="font-medium">
                {currencyFormatter.format(newAward)}
              </span>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:justify-end sm:space-x-0">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <SubmitButton
              buttonTxt="Update Award"
              status={mutation.status}
              size="md"
              className="w-auto"
            />
          </DialogFooter>
        </form>
      </Form>
    </DialogContent>
  )
}
